import type { CursorData } from '@alpod-ai-writer/shared';
import { useDebounceCallback } from '@react-hook/debounce';
import type { TElement, Value } from '@udecode/plate';
import { Plate, PlateContent, type PlateContentProps,type PlateEditor,useEditorContainerRef, useEditorRef } from '@udecode/plate/react';
import clsx from 'clsx';
import { BoldIcon,HighlighterIcon,ItalicIcon,SubscriptIcon,SuperscriptIcon,UnderlineIcon } from 'lucide-react';
import React, { type LegacyRef,useCallback, useEffect } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { createPortal } from 'react-dom';
import { Node } from 'slate';
import * as Y from 'yjs';

import { detectOS } from '../../lib/responsivity.utils';
import { useDocumentEditor } from '../hooks/useDocumentEditor.hook';
import { useSectionNav } from '../hooks/useSectionNav.hook';
import { useTextEditor } from '../hooks/useTextEditor.hook';
import { SuggestionToolbar } from './common/suggestionToolbar.component';
import { Toolbar, ToolbarGroup } from './common/toolbar.component';
import { AiToolbarButtonAccent } from './toolbar/aiToolbarButtonAccent.component';
import { AlignDropdownMenu } from './toolbar/alignDropdownMenu.component';
import { RedoToolbarButton, UndoToolbarButton } from './toolbar/historyToolbarButton.component';
import { ImageToolbarButton } from './toolbar/imageToolbarButton.component';
import { BulletedIndentListToolbarButton, NumberedIndentListToolbarButton } from './toolbar/indentListToolbarButton.component';
import { InsertDropdownMenu } from './toolbar/insertDropdownMenu.component';
import { LinkToolbarButton } from './toolbar/linkToolbarButton.component';
import { MarkToolbarButton } from './toolbar/markToolbarButton.component';
import { SuggestionToolbarButton } from './toolbar/suggestionToolbarButton.component';
import { TableDropdownMenu } from './toolbar/tableDropdownMenu.component';
import { TurnIntoDropdownMenu } from './toolbar/turnIntoDropdownMenu.component';

export const TextEditor: React.FC<{
    documentField: string;
    documentFieldList: string[];
    cursorData: CursorData;
    yjsNode: Y.XmlText;
    toolbarEl?: HTMLDivElement|null;
    displayWordCount?: boolean;
    overrideReadOnly?: boolean;
    className?: string;
    preloadedContent?: TElement | null;
}> = ({
    documentField,
    documentFieldList,
    cursorData,
    yjsNode,
    toolbarEl,
    displayWordCount = false,
    overrideReadOnly = false,
    className,
    preloadedContent,
}) => {
    const docEditor = useDocumentEditor();
    const textEditor = useTextEditor({
        yjsProvider: docEditor.yjsProvider,
        document: docEditor.document,
        cursorData,
        documentField,
        documentFieldList,
        initialValue: { type: 'p' },
        getYjsDocumentNode: () => yjsNode,
        preloadedContent: preloadedContent ?? undefined,
    });

    const { currentSection } = useSectionNav(docEditor.template.config.sections);
    const [ wordCount, setWordCount ] = React.useState(0);
    const [ suggestionCount, setSuggestionCount ] = React.useState(textEditor.api.suggestion.countSuggestions());

    const isReadOnly = docEditor.readOnly || overrideReadOnly;

    const setAwernessSelection = useCallback(() => {
        docEditor.yjsProvider.awareness?.setLocalStateField('selection_field', documentField);
    }, [ docEditor ]);

    const updateCounts = useCallback(({ value }: { editor: PlateEditor, value: Value }) => {
        if (displayWordCount) {
            const newWordCount = countWords(value);
            setWordCount(newWordCount);
        }

        setSuggestionCount(textEditor.api.suggestion.countSuggestions());
    }, [ displayWordCount, textEditor ]);

    const handleValueChange = useDebounceCallback(updateCounts, 500, true);

    const acceptAllSuggestions = useCallback(() => {
        textEditor.api.suggestion.acceptAllSuggestions();
    }, [ textEditor ]);

    const rejectAllSuggestions = useCallback(() => {
        textEditor.api.suggestion.rejectAllSuggestions();
    }, [ textEditor ]);

    useEffect(() => {
        if (displayWordCount && textEditor) {
            const initialWordCount = countWords(textEditor.children);
            setWordCount(initialWordCount);
        }
    }, [ displayWordCount, textEditor ]);

    useEffect(() => {
        if (currentSection?.headingId) {
            // Scroll to the entry when it's focused
            const targetEl = document.getElementById(`${currentSection.headingId}`);
            
            if (targetEl) {
                targetEl.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                    inline: 'nearest'
                });
            } else {
                setTimeout(() => {
                    const targetEl = document.getElementById(`${currentSection.headingId}`);
                    if (targetEl) {
                        targetEl.scrollIntoView({
                            behavior: 'smooth',
                            block: 'start',
                            inline: 'nearest'
                        });
                    }
                }, 500);
            }
        }
    }, [ currentSection ]);

    return (
        <Plate
            editor={textEditor}
            readOnly={isReadOnly}
            primary
            onValueChange={handleValueChange}
        >
            {!(isReadOnly) && toolbarEl && createPortal(
                <>
                    <Toolbar>
                        <ToolbarGroup>
                            <UndoToolbarButton tooltip='Zpět' />
                            <RedoToolbarButton tooltip='Vpřed' />
                        </ToolbarGroup>
                        <ToolbarGroup>
                            <TurnIntoDropdownMenu />
                        </ToolbarGroup>
                        <ToolbarGroup>
                            <MarkToolbarButton nodeType='bold'tooltip='Tučné písmo'>
                                <BoldIcon />
                            </MarkToolbarButton>
                            <MarkToolbarButton nodeType='italic' tooltip='Kurzíva'>
                                <ItalicIcon />
                            </MarkToolbarButton>
                            <MarkToolbarButton nodeType='underline' tooltip='Podtržené'>
                                <UnderlineIcon />
                            </MarkToolbarButton>
                            <MarkToolbarButton nodeType='highlight' tooltip='Zvýraznění'>
                                <HighlighterIcon />
                            </MarkToolbarButton>
                            <MarkToolbarButton nodeType='superscript' tooltip='Horní index'>
                                <SuperscriptIcon />
                            </MarkToolbarButton>
                            <MarkToolbarButton nodeType='subscript' tooltip='Dolní index'>
                                <SubscriptIcon />
                            </MarkToolbarButton>
                        </ToolbarGroup>
                        <ToolbarGroup>
                            <AlignDropdownMenu />
                            <NumberedIndentListToolbarButton />
                            <BulletedIndentListToolbarButton />
                        </ToolbarGroup>
                        <ToolbarGroup>
                            <LinkToolbarButton />
                            <ImageToolbarButton />
                            <TableDropdownMenu />
                            <InsertDropdownMenu />
                        </ToolbarGroup>
                        <ToolbarGroup className='order-[-1] lg:order-none pr-2 lg:pr-0'>
                            <SuggestionToolbarButton />
                            <span className='inline-block w-2 flex-shrink-0' />
                            <AiToolbarButtonAccent />
                        </ToolbarGroup>
                    </Toolbar>
                </>,
                toolbarEl
            )}
            <EditorContainer className={clsx(
                '[&_.slate-selection-area]:border [&_.slate-selection-area]:border-primary [&_.slate-selection-area]:bg-primary/10',
                className
            )} onFocus={setAwernessSelection}>
                {suggestionCount > 0 && (
                    <SuggestionToolbar
                        suggestionCount={suggestionCount}
                        onAcceptAll={acceptAllSuggestions}
                        onRejectAll={rejectAllSuggestions}
                        className='mb-6'
                    />
                )}

                <EditorInner
                    placeholder={
                        {
                            'Windows': 'Začněte psát nebo stiskněte Win+J pro použít AI...',
                            'Macintosh': 'Začněte psát nebo stiskněte Cmd+J pro použít AI...',
                            'Linux': 'Začněte psát nebo stiskněte Meta+J pro použít AI...',
                            'Other': 'Začněte psát...',
                        }[detectOS()]
                    }
                    className='outline-none'
                />
                {displayWordCount && (
                    <div className='text-sm text-right text-gray-600 mt-4'>
                        Počet slov: <span className='font-semibold'>{wordCount}</span>
                    </div>
                )}
            </EditorContainer>
        </Plate>
    );
};

export const EditorContainer: React.FC<React.HTMLAttributes<HTMLDivElement>> = (props) => {
    const editor = useEditorRef();
    const containerRef = useEditorContainerRef();
    return (
        <div id={editor.uid} ref={containerRef as LegacyRef<HTMLDivElement>} {...props} />
    );
};

export const EditorInner = React.forwardRef<HTMLDivElement, PlateContentProps>(
    ({ ...props }, ref) => {
        return (
            <DndProvider backend={HTML5Backend}>
                <PlateContent
                    ref={ref}
                    {...props}
                />
            </DndProvider>
        );
    }
);

function countWords(value: Value): number {
    const text = Node.string({ children: value }) || '';
    const wordCount = text.trim().split(/[\s,\.]+/).filter(Boolean).length;
    return wordCount;
}
